import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, CheckCheck, ShieldCheck, Clock } from "lucide-react";
import QRCode from "./QRCode";

const Bubble = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="ss-msg-in relative max-w-[86%] rounded-2xl rounded-tl-md bg-[#202c33] px-3 py-2 text-[13px] leading-snug text-white shadow"
  >
    {children}
    <span className="mt-1 block text-right font-mono text-[9px] text-white/40">
      <CheckCheck size={11} className="inline text-[#53bdeb]" /> ora
    </span>
  </div>
);

const PHONE_MOTION = {
  initial: { rotateX: 6, rotateY: -6 },
  animate: { rotateX: 0, rotateY: 0 },
  whileHover: { rotateY: 3, rotateX: -2 },
  transition: { type: "spring", stiffness: 120, damping: 14 },
};
const TYPING_MOTION = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 } };
const CHAT_BG = {
  backgroundColor: "#0b141a",
  backgroundImage: "radial-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)",
  backgroundSize: "18px 18px",
};
const NOTIFY_PHASES = ["notified", "payment", "booked"];

const SimulatorHeader = ({ winningUser }) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <p className="font-mono text-[11px] uppercase tracking-widest text-[#25D366]">
        // User Simulator
      </p>
      <h2 className="font-head text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">
        WhatsApp Live
      </h2>
    </div>
    {winningUser && (
      <span className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] text-zinc-400">
        {winningUser.name} · {winningUser.distance_m}m
      </span>
    )}
  </div>
);

const IdlePlaceholder = () => (
  <div className="m-auto text-center px-6">
    <Clock size={26} className="mx-auto mb-2 text-zinc-600" />
    <p className="font-body text-xs text-zinc-500">
      Waiting for a free slot…<br />Press the red button to simulate.
    </p>
  </div>
);

const TypingIndicator = () => (
  <motion.div
    key="typing"
    {...TYPING_MOTION}
    className="flex w-16 items-center gap-1 rounded-2xl rounded-tl-md bg-[#202c33] px-3 py-3"
    data-testid="typing-indicator"
  >
    {["0s", "0.2s", "0.4s"].map((d) => (
      <span key={d} className="ss-dot h-1.5 w-1.5 rounded-full bg-white/70" style={{ animationDelay: d }} />
    ))}
  </motion.div>
);

const NotificationMessage = ({ slot, phase, onBookNow, isLocking, secondsLeft }) => (
  <Bubble testid="whatsapp-notification">
    <p className="font-head text-base font-extrabold uppercase tracking-tight text-[#FFCC00]">
      Here we go! 🚨
    </p>
    <p className="mt-1">
      The <b>{slot.court_label}</b> at <b>{slot.provider_name}</b> just opened up! {slot.time_label}.
    </p>
    <p className="mt-1.5 font-mono text-[13px]">
      Price:{" "}
      <span className="font-bold text-[#25D366]">€{slot.price_discounted.toFixed(0)}</span>{" "}
      <span className="text-white/40 line-through">€{slot.price_standard.toFixed(0)}</span>
    </p>
    {phase !== "booked" && (
      <>
        <button
          data-testid="book-now-button"
          onClick={onBookNow}
          disabled={isLocking || phase === "payment"}
          className="mt-2.5 w-full rounded-xl bg-[#25D366] py-2 font-head text-sm font-bold uppercase tracking-wide text-[#052e16] transition-transform hover:scale-[1.02] disabled:opacity-60"
        >
          {isLocking ? "Locking slot…" : phase === "payment" ? "Processing…" : "Book Now"}
        </button>
        <p className="mt-1.5 flex items-center justify-center gap-1 font-mono text-[10px] text-white/50">
          <Clock size={10} /> Offer valid for {secondsLeft}s more · first “Yes” wins
        </p>
      </>
    )}
  </Bubble>
);

const ConfirmationMessage = ({ receipt }) => (
  <div
    data-testid="whatsapp-confirmation"
    className="ss-msg-in max-w-[86%] self-end rounded-2xl rounded-tr-md bg-[#005c4b] px-3 py-2 text-[13px] text-white"
  >
    <p className="flex items-center gap-1.5 font-head text-sm font-bold uppercase text-[#c9f0dc]">
      <ShieldCheck size={15} /> Booking confirmed
    </p>
    <p className="mt-1">
      {receipt.court_label} · {receipt.provider_name}
    </p>
    <div className="mt-2 flex justify-center rounded-lg bg-white/95 p-2">
      <QRCode value={receipt.qr_payload} size={128} />
    </div>
    <p className="mt-1.5 text-center font-mono text-[11px] text-[#c9f0dc]">
      Code: {receipt.booking_id}
    </p>
    <span className="mt-1 block text-right font-mono text-[9px] text-white/50">
      <Check size={11} className="inline" /> ora
    </span>
  </div>
);

const MessageList = ({ phase, slot, onBookNow, isLocking, secondsLeft, receipt }) => (
  <div
    className="ss-scroll flex flex-col gap-2 px-3 py-4 min-h-[300px] max-h-[380px] overflow-y-auto"
    style={CHAT_BG}
    data-testid="whatsapp-chat"
  >
    {phase === "idle" && <IdlePlaceholder />}
    <AnimatePresence>{phase === "typing" && <TypingIndicator />}</AnimatePresence>
    {NOTIFY_PHASES.includes(phase) && slot && (
      <NotificationMessage
        slot={slot}
        phase={phase}
        onBookNow={onBookNow}
        isLocking={isLocking}
        secondsLeft={secondsLeft}
      />
    )}
    {phase === "booked" && receipt && <ConfirmationMessage receipt={receipt} />}
  </div>
);

export const WhatsAppSimulator = ({
  phase,
  slot,
  winningUser,
  secondsLeft,
  onBookNow,
  isLocking,
  receipt,
}) => (
  <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 sm:p-6" data-testid="user-panel">
    <SimulatorHeader winningUser={winningUser} />

    {/* Phone mockup */}
    <motion.div {...PHONE_MOTION} style={{ perspective: 1000 }} className="mx-auto w-full max-w-[340px]">
      <div className="rounded-[2.4rem] border-[6px] border-[#2a2a2a] bg-[#0b141a] p-0 shadow-2xl overflow-hidden">
        {/* notch */}
        <div className="relative flex justify-center bg-[#0b141a] pt-2">
          <div className="h-1.5 w-24 rounded-full bg-black/60" />
        </div>
        {/* WA header */}
        <div className="flex items-center gap-3 bg-[#202c33] px-4 py-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] font-head font-bold text-[#0a0a0a]">
            S
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">SlotSaver</p>
            <p className="text-[10px] text-[#25D366]">business account · online</p>
          </div>
        </div>

        <MessageList
          phase={phase}
          slot={slot}
          onBookNow={onBookNow}
          isLocking={isLocking}
          secondsLeft={secondsLeft}
          receipt={receipt}
        />
      </div>
    </motion.div>
  </div>
);

export default WhatsAppSimulator;
